import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import qs from 'qs';
import { observer } from 'mobx-react';
import { useSnackbar } from 'notistack';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Loading from 'components/common/loading';
import domainData from 'store/domainStore';
import DateRangeFilter from './DateRangeFilter';
import EventFilter from './EventFilter';
import SPFilter from './SPFilter';
import KPIGlobalSection from './content/KPIGlobalSection';
import KPISection from './content/KPISection';
import PieChartSection from './content/PieChartSection';
import DataTableSection from './content/DataTableSection';
import LineChartSection from './content/LineChartSection';
import M from 'messages';
import { replacePath } from 'helpers/helper';
import classes from './content/styles';

const DashboardLayout = observer(({ dashboardStore }) => {
  const [spLoading, setSpLoading] = useState(false);
  const [spLoaded, setSpLoaded] = useState(false);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuLoaded, setMenuLoaded] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const isUserSuperAdmin = domainData.userIsSuperAdmin();
  const navigate = useNavigate();

  const setFilter = filter => {
    replacePath(navigate, { params: filter });
    dashboardStore.setFilter({ params: filter });
  };

  const pageParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const queryParams = dashboardStore.getCurrentFilterParams(pageParams);

  const fetchDashboardData = async () => {
    dashboardStore.fetchTimeKPIData(queryParams);
    dashboardStore.fetchVisitsByWineColors(queryParams);
    dashboardStore.fetchViewsByWineSweetness(queryParams);
    dashboardStore.fetchViewsByWineStyle(queryParams);
    dashboardStore.fetchViewsByCountry(queryParams);
    dashboardStore.fetchClicksByWine(queryParams);
    dashboardStore.fetchAverageRatingByWine(queryParams);
    dashboardStore.fetchEngagementOverTime(queryParams);
  };

  const fetchDashboardGlobalData = async () => {
    dashboardStore.fetchKPIGlobalData(queryParams);
  };

  const fetchSpData = async () => {
    setSpLoading(true);
    setSpLoaded(false);
    let spId = null;
    if (isUserSuperAdmin) {
      const spNames = await domainData.getSPListNames();
      dashboardStore.setServiceProviders(spNames);
      const queryParamsSPId = spNames.find(item => item.id === queryParams.params.currentSPId);
      spId = queryParamsSPId?.id || spNames[0].id;
    } else {
      const currentSP = await domainData.getCurrentSP();
      spId = currentSP;
    }
    setFilter({
      ...queryParams.params,
      currentSPId: Number(spId),
    });
    setSpLoading(false);
    setSpLoaded(true);
  };

  const fetchMenuData = async () => {
    try {
      setMenuLoading(true);
      setMenuLoaded(false);
      const menus = await dashboardStore.fetchMenus(queryParams);
      const queryParamsMenuId = menus.find(item => item.id === queryParams.params.selectedMenuId);
      setFilter({
        ...queryParams.params,
        selectedMenuId: queryParamsMenuId?.id || menus.filter(item => item.isDefault)?.[0]?.id,
      });
    } catch (error) {
      enqueueSnackbar('Get wine lists error', { variant: 'error' });
    } finally {
      setMenuLoading(false);
      setMenuLoaded(true);
    }
  };

  useEffect(() => {
    // Fetch sp data
    fetchSpData();
    return () => {
      dashboardStore.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Fetch menu data
    if (spLoaded && queryParams.params.currentSPId) {
      fetchMenuData();
      fetchDashboardGlobalData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.params.currentSPId, spLoaded]);

  useEffect(() => {
    if (menuLoaded && queryParams.params.selectedMenuId) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryParams.params.selectedMenuId,
    queryParams.params.dateRange.start,
    queryParams.params.dateRange.end,
    menuLoaded,
  ]);

  const onDateRangeChange = (range, dateDropdownType) => {
    setFilter({ ...queryParams.params, dateRange: range, dateDropdownType });
  };

  const onMenuChange = menu => {
    setFilter({ ...queryParams.params, selectedMenuId: menu.id });
  };

  const onSPChange = async sp => {
    setFilter({ ...queryParams.params, currentSPId: Number(sp.id) });
  };

  return (
    <Grid>
      {spLoading ? (
        <Loading />
      ) : (
        <>
          <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            {/* Title on the left */}
            <Grid>
              <Typography sx={classes.globalMetricsTitle}>{M.get('dashboard.globalMetrics')}</Typography>
            </Grid>

            {/* DateRangeFilter on the right */}
            <Grid container>
              <Grid>{isUserSuperAdmin && <SPFilter onSPChange={onSPChange} queryParams={queryParams} />}</Grid>
            </Grid>
          </Grid>
          <KPIGlobalSection />
        </>
      )}
      {menuLoading ? (
        <Loading />
      ) : dashboardStore.menus.length === 0 ? (
        <Typography variant="h6" align="center" sx={{ mt: 30 }}>
          No data available
        </Typography>
      ) : (
        <>
          <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2, mt: 2 }}>
            {/* Title on the left */}
            <Grid>
              <Typography sx={classes.globalMetricsTitle}>{M.get('dashboard.timeBasedMetrics')}</Typography>
            </Grid>

            {/* DateRangeFilter on the right */}
            <Grid container>
              <Grid>{<EventFilter onMenuChange={onMenuChange} queryParams={queryParams} />}</Grid>
              <Grid>
                <DateRangeFilter onDateRangeChange={onDateRangeChange} queryParams={queryParams} />
              </Grid>
            </Grid>
          </Grid>
          <KPISection />
          <LineChartSection queryParams={queryParams} />
          <PieChartSection />
          <DataTableSection />
        </>
      )}
    </Grid>
  );
});

DashboardLayout.propTypes = {
  dashboardStore: PropTypes.object.isRequired,
};

export default DashboardLayout;
